// /u/:nickname — a contributor's profile: avatar, bio and link, stats (songs, played, rating,
// Hall of Fame rank, favourite genre), the top-rated song and all songs with their notes, play
// times, ratings and Wizard marks. Your own profile can be edited in place.
import { useEffect, useMemo, useState } from "react";
import { BIO_MAX, checkBio, checkProfileLink, clockFor, type Bpm, type LoopData } from "@eb/shared";
import { api, ApiError } from "../api";
import { Link, useTitle } from "../lib/router";
import { MiniNotes } from "../river/MiniNotes";
import { fetchServerTime } from "../river/player";
import { formatPlayTime } from "../river/slots";
import { saveProfile, useSession } from "../session";
import { Avatar } from "../ui/Avatar";
import { StarAverage } from "../ui/Stars";

interface Song {
  id: string;
  /** Profiles show songs of both streams (125 BPM and the 72 BPM site). */
  bpm: Bpm;
  day: number;
  slot: number | null;
  title: string | null;
  wizardShare: number;
  genre: string | null;
  rating: { average: number | null; count: number };
}

interface Profile {
  nickname: string;
  joined: string;
  bio: string;
  link: string;
  fame: { average: number | null; count: number; score: number };
  stats: { songs: number; played: number; rank: number | null; genre: string | null };
  topSongId: string | null;
  loops: Song[];
}

export function ProfilePage({ nickname }: { nickname: string }) {
  useTitle(nickname);
  const me = useSession((s) => s.user);
  const [p, setP] = useState<Profile | null>(null);
  const [notes, setNotes] = useState<Map<string, LoopData>>(new Map());
  const [epoch, setEpoch] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setP(null);
    setError("");
    api<Profile>("GET", `/api/users/${encodeURIComponent(nickname)}`)
      .then(async (prof) => {
        setP(prof);
        const ids = prof.loops.slice(0, 64).map((l) => l.id);
        if (!ids.length) return;
        const r = await api<{ loops: { id: string; data: LoopData }[] }>(
          "GET",
          `/api/loops?ids=${ids.map(encodeURIComponent).join(",")}`,
        );
        setNotes(new Map(r.loops.map((l) => [l.id, l.data])));
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
    void fetchServerTime().then((t) => setEpoch(t.epochMs));
  }, [nickname]);

  const own = !!me && !!p && me.nickname === p.nickname;
  const top = useMemo(() => p?.loops.find((l) => l.id === p.topSongId) ?? null, [p]);

  if (error)
    return (
      <main id="main" className="page narrow">
        <p role="alert">{error}</p>
      </main>
    );
  if (!p)
    return (
      <main id="main" className="page narrow">
        <p className="muted">Loading…</p>
      </main>
    );

  const playTime = (s: Song) => {
    if (epoch === null || s.slot === null) return "time fixed at freeze";
    const at = clockFor(s.bpm ?? 125).slotStartMs(s.day, s.slot, epoch);
    return `${at <= Date.now() ? "played" : "plays"} ${formatPlayTime(at)}`;
  };

  return (
    <main id="main" className="page profile-page">
      <section className="profile-head panel" aria-label="Profile">
        <Avatar nickname={p.nickname} size={96} />
        <div className="profile-id">
          <h1>{p.nickname}</h1>
          <p className="muted">
            Contributor since{" "}
            {new Date(p.joined).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
          </p>
          <p className="row profile-badges">
            {p.stats.rank !== null && (
              <Link href="/fame" className={`chip fame-chip${p.stats.rank <= 3 ? ` top${p.stats.rank}` : ""}`}>
                #{p.stats.rank} in the Hall of Fame
              </Link>
            )}
            {p.stats.genre && <span className="chip">Mostly {p.stats.genre}</span>}
          </p>
          {editing ? (
            <ProfileEditor
              bio={p.bio}
              link={p.link}
              onCancel={() => setEditing(false)}
              onSaved={(bio, link) => {
                setP({ ...p, bio, link });
                setEditing(false);
              }}
            />
          ) : (
            <>
              {p.bio ? (
                <p className="profile-bio" data-testid="profile-bio">
                  {p.bio}
                </p>
              ) : (
                own && <p className="muted">Tell listeners who you are: add a short bio.</p>
              )}
              {p.link && (
                <p>
                  <a href={p.link} rel="nofollow ugc noopener noreferrer" target="_blank" data-testid="profile-link">
                    {p.link.replace(/^https:\/\//, "")}
                  </a>
                </p>
              )}
              {own && (
                <button
                  type="button"
                  className="btn btn-small"
                  onClick={() => setEditing(true)}
                  data-testid="profile-edit"
                >
                  Edit profile
                </button>
              )}
            </>
          )}
        </div>
      </section>

      <section className="profile-stats" aria-label="Stats">
        <div className="stat panel">
          <span className="stat-value">{p.stats.songs}</span>
          <span className="stat-label">songs</span>
        </div>
        <div className="stat panel">
          <span className="stat-value">{p.stats.played}</span>
          <span className="stat-label">played</span>
        </div>
        <div className="stat panel" data-testid="profile-fame">
          <StarAverage average={p.fame.average} count={p.fame.count} size={18} />
          <span className="stat-label">{p.fame.count > 0 ? `fame score ${p.fame.score.toFixed(2)}` : "rating"}</span>
        </div>
      </section>

      {top && (
        <section className="profile-top panel" aria-labelledby="top-song">
          <h2 id="top-song">Top song</h2>
          <SongCard s={top} notes={notes.get(top.id)} playTime={playTime(top)} wide />
        </section>
      )}

      <section aria-labelledby="songs">
        <h2 id="songs">Songs</h2>
        {p.loops.length === 0 && <p>No songs yet.</p>}
        <ul className="profile-songs">
          {p.loops.map((s) => (
            <li key={s.id}>
              <SongCard s={s} notes={notes.get(s.id)} playTime={playTime(s)} />
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}

function SongCard({ s, notes, playTime, wide }: { s: Song; notes?: LoopData; playTime: string; wide?: boolean }) {
  return (
    <article className="song-card panel">
      <Link href={`/loop/${encodeURIComponent(s.id)}`} className="song-title">
        {s.title ?? "Untitled song"}
      </Link>
      <MiniNotes
        loop={notes}
        width={wide ? 560 : 280}
        height={wide ? 90 : 56}
        label={`Notes of ${s.title ?? "an untitled song"}`}
      />
      <div className="song-meta">
        <span className="muted small">{playTime}</span>
        {s.genre && <span className="chip">{s.genre}</span>}
        {s.bpm === 72 && <span className="chip">72 BPM</span>}
        {s.wizardShare > 0 && <span className="wizard-badge">✨ {Math.round(s.wizardShare * 100)} % Wizard</span>}
        <StarAverage average={s.rating.average} count={s.rating.count} />
      </div>
    </article>
  );
}

function ProfileEditor({
  bio: bio0,
  link: link0,
  onCancel,
  onSaved,
}: {
  bio: string;
  link: string;
  onCancel: () => void;
  onSaved: (bio: string, link: string) => void;
}) {
  const [bio, setBio] = useState(bio0);
  const [link, setLink] = useState(link0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const bioCheck = checkBio(bio);
  const linkCheck = checkProfileLink(link);
  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const u = await saveProfile(bio, link);
      onSaved(u.bio, u.link);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't save your profile.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <form
      className="stack profile-editor"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <label className="stack-label">
        <span>Bio</span>
        <textarea
          rows={4}
          maxLength={BIO_MAX + 20}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          aria-invalid={!bioCheck.ok}
          aria-describedby="bio-help"
          data-testid="profile-bio-input"
        />
        <span id="bio-help" className={`small ${bioCheck.ok ? "muted" : "error"}`}>
          {bioCheck.ok ? `${bio.trim().length} / ${BIO_MAX}` : bioCheck.reason}
        </span>
      </label>
      <label className="stack-label">
        <span>Link (optional, https://…)</span>
        <input
          type="url"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          aria-invalid={!linkCheck.ok}
          placeholder="https://"
          data-testid="profile-link-input"
        />
        {!linkCheck.ok && <span className="small error">{linkCheck.reason}</span>}
      </label>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <div className="row">
        <button
          className="btn btn-primary"
          disabled={saving || !bioCheck.ok || !linkCheck.ok}
          data-testid="profile-save"
        >
          {saving ? "Saving…" : "Save profile"}
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
